import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import io from "socket.io-client";
import queryString from "../query-string";
import {
  setFilter, setDateFilter, setUnitFilter, setAllFilter, setFilterTimeframe,
} from "../features/callPlayer/callPlayerSlice";
import {
  getCalls, addCall, getOlderCalls, getNewerCalls, playedCall, nextCallToPlay, setTranscript,
} from "../features/calls/callsSlice";
import { previousCallToPlay } from "./demoHelpers";
import { useGetSystemsQuery, useGetGroupsQuery, useGetTalkgroupsQuery } from "../features/api/apiSlice";
import DemoFeed from "./DemoFeed";
import DemoPlayer from "./DemoPlayer";
import DemoChips from "./DemoChips";
import DemoTopBar from "./DemoTopBar";
import Activity from "../Call/Activity";
import "./demo.css";

const socket = io(process.env.REACT_APP_BACKEND_SERVER, { withCredentials: true });
const SCROLL_TOP_THRESHOLD = 8;

function DemoCalls() {
  const { shortName } = useParams();
  const { data: allSystems } = useGetSystemsQuery();
  const { data: groupsData } = useGetGroupsQuery(shortName);
  const { data: talkgroupsData } = useGetTalkgroupsQuery(shortName);
  const { data: callsData } = useSelector((state) => state.calls);
  const callsLoading = useSelector((state) => state.calls.loading);

  const [live, setLive] = useState(true);
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [selectCallId, setSelectCallId] = useState(false);
  const [initialCallId, setInitialCallId] = useState(false);
  const [urlOptions, setUrlOptions] = useState(false);
  const [currentCall, setCurrentCall] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [silenceCount, setSilenceCount] = useState(0);
  const [activityVisible, setActivityVisible] = useState(false);
  const [statusDismissed, setStatusDismissed] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wasConnectedRef = useRef(true);
  const uri = queryString.parse(useLocation().search);
  const pathname = useLocation().pathname;

  const filterType = useSelector((s) => s.callPlayer.filterType);
  const filterGroupId = useSelector((s) => s.callPlayer.filterGroupId);
  const filterTalkgroups = useSelector((s) => s.callPlayer.filterTalkgroups);
  const filterUnit = useSelector((s) => s.callPlayer.filterUnit);
  const filterRows = useSelector((s) => s.callPlayer.filterRows);
  const filterTimeframe = useSelector((s) => s.callPlayer.filterTimeframe);
  const filterStarred = useSelector((s) => s.callPlayer.filterStarred);
  const filterDate = useSelector((s) => s.callPlayer.filterDate);
  const backgroundAutoplay = useSelector((s) => s.callPlayer.backgroundAutoplay);

  const currentCallId = currentCall ? currentCall._id : false;

  const system = useMemo(() => {
    if (!allSystems) return false;
    return allSystems.systems.find((s) => s.shortName === shortName) || false;
  }, [allSystems, shortName]);

  // --- filter description + socket (verbatim from Calls.js) ---
  const getFilterDescription = () => {
    let filter = { type: "all", code: "", filterStarred: false };
    switch (filterType) {
      case 1: filter.type = "group"; filter.code = filterGroupId; break;
      case 2: filter.type = "talkgroup"; filter.code = filterTalkgroups; break;
      case 3: filter.type = "unit"; filter.code = filterUnit; break;
      default: filter.type = "all"; filter.code = "";
    }
    filter.filterStarred = filterStarred;
    return filter;
  };

  const startSocket = () => {
    const filter = getFilterDescription();
    const hasTimeframe = filterTimeframe && (filterTimeframe.from || filterTimeframe.to);
    const advancedSpec = ((filterRows && filterRows.length > 0) || hasTimeframe)
      ? JSON.stringify({
          rows: filterRows || [],
          ...(hasTimeframe ? { timeframe: { from: filterTimeframe.from || 0, to: filterTimeframe.to || 0 } } : {}),
        })
      : undefined;
    socket.emit("start", {
      filterCode: filter.code, filterType: filter.type, filterName: "OpenMHz",
      filterStarred: filter.filterStarred, filter: advancedSpec, shortName,
    });
  };
  const stopSocket = () => socket.emit("stop");

  const handleSocketMessage = (data) => {
    const message = JSON.parse(data);
    if (message.type === "calls") {
      setSelectCallId(message._id);
      dispatch(addCall(message));
    }
  };

  const handleTranscript = (data) => {
    const msg = JSON.parse(data);
    dispatch(setTranscript({ _id: msg._id, transcript: msg.transcript }));
  };

  // --- URI read (setStateFromUri) — copy verbatim from Calls.js, replacing
  //     "/system/" deep-link fetch path unchanged (it targets the backend, not
  //     the route). Keep the call-time anchor lookup. ---
  const setStateFromUri = async () => {
    let filter = {
      filterDate: false, filterType: 0, filterTalkgroups: [], filterGroupId: false,
      filterUnit: "", filterRows: [], filterTimeframe: { from: false, to: false },
      filterStarred: false, filterCallId: false, shortName,
    };
    if (uri.hasOwnProperty("filter")) {
      try {
        const parsed = JSON.parse(uri["filter"]);
        if (parsed && Array.isArray(parsed.rows)) filter.filterRows = parsed.rows;
        if (parsed && parsed.timeframe && (parsed.timeframe.from || parsed.timeframe.to)) {
          filter.filterTimeframe = { from: parsed.timeframe.from || false, to: parsed.timeframe.to || false };
          setLive(false);
        }
      } catch (err) { console.warn("bad filter param: " + err); }
      if (!urlOptions) setUrlOptions(true);
    }
    if (uri.hasOwnProperty("starred")) {
      filter.filterStarred = uri["starred"] === "true";
      if (!urlOptions) setUrlOptions(true);
    }
    if (uri.hasOwnProperty("time")) {
      filter.filterDate = new Date(parseInt(uri["time"])).getTime();
      setLive(false);
      if (!urlOptions) setUrlOptions(true);
    }
    if (uri.hasOwnProperty("call-id")) {
      const _id = uri["call-id"];
      setInitialCallId(_id);
      setAutoPlay(false);
      setLive(false);
      if (!urlOptions) setUrlOptions(true);
      try {
        const res = await fetch(process.env.REACT_APP_BACKEND_SERVER + "/" + shortName + "/call/" + _id);
        const data = await res.json();
        if (data && data.success && data.call && data.call.time) {
          filter.filterDate = new Date(data.call.time).getTime() + 1;
        }
      } catch (err) { console.warn("call lookup failed: " + err); }
    }
    if (uri.hasOwnProperty("filter-code") && uri.hasOwnProperty("filter-type")) {
      if (!urlOptions) setUrlOptions(true);
      if (uri["filter-type"] === "group") { filter.filterType = 1; filter.filterGroupId = uri["filter-code"]; }
      if (uri["filter-type"] === "talkgroup") { filter.filterType = 2; filter.filterTalkgroups = uri["filter-code"].split(",").map(Number); }
      if (uri["filter-type"] === "unit") { filter.filterType = 3; filter.filterUnit = uri["filter-code"]; }
    }
    dispatch(setFilter(filter));
  };

  // --- URI write (updateUri) — copy verbatim from Calls.js but navigate to the
  //     /demo path instead of /system. ---
  const updateUri = () => {
    let search = "?";
    switch (filterType) {
      case 1: search += `filter-type=group&filter-code=${filterGroupId}`; break;
      case 2: search += `filter-type=talkgroup&filter-code=${filterTalkgroups}`; break;
      case 3: search += `filter-type=unit&filter-code=${encodeURIComponent(filterUnit)}`; break;
      default: break;
    }
    const hasTimeframe = filterTimeframe && (filterTimeframe.from || filterTimeframe.to);
    if ((filterRows && filterRows.length > 0) || hasTimeframe) {
      const spec = { rows: filterRows || [] };
      if (hasTimeframe) spec.timeframe = { from: filterTimeframe.from || 0, to: filterTimeframe.to || 0 };
      if (search.length !== 1) search += "&";
      search += `filter=${encodeURIComponent(JSON.stringify(spec))}`;
    }
    if (initialCallId) { if (search.length !== 1) search += "&"; search += `call-id=${initialCallId}`; }
    if (filterDate) { if (search.length !== 1) search += "&"; search += `time=${filterDate}`; }
    if (filterStarred) { if (search.length !== 1) search += "&"; search += `starred=true`; }
    navigate(pathname + search, { replace: true });
  };

  const handleLiveToggle = () => {
    if (!live) {
      dispatch(setDateFilter(false));
      dispatch(setFilterTimeframe({ from: false, to: false }));
      setLive(true);
    }
  };

  // Activity chart cell click: filter to that talkgroup at that time (from Calls.js).
  const handleActivityNavigate = (tgNum, timestamp) => {
    dispatch(setFilter({
      filterDate: timestamp, filterType: 2, filterTalkgroups: [tgNum],
      filterGroupId: false, filterStarred: false, shortName,
    }));
    setLive(false);
    stopSocket();
    setActivityVisible(false);
  };
  const handleArchivePick = (ms) => { dispatch(setDateFilter(ms)); setLive(false); stopSocket(); };

  let archiveLabel = "Archive";
  if (filterDate) {
    const d = new Date(filterDate);
    archiveLabel = d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  const handleNewer = () => { if (!live) dispatch(getNewerCalls({})); };
  const handleOlder = () => dispatch(getOlderCalls({}));

  // --- playback (from CallPlayer.js) ---
  const playCall = useCallback((data) => {
    setCurrentCall(data.call);
    dispatch(playedCall(data.call._id));
    setIsPlaying(true);
  }, [dispatch]);

  const callEnded = () => {
    if (!callsData) return;
    if (autoPlay) {
      const nextCall = nextCallToPlay(callsData, currentCallId);
      if (nextCall) {
        setCurrentCall(nextCall);
        dispatch(playedCall(nextCall._id));
        setIsPlaying(true);
      } else if (backgroundAutoplay) {
        // No more calls to play, but keep the tab awake by playing silence so a
        // freshly-arriving live call still autoplays (matches CallPlayer.js).
        setSilenceCount((c) => c + 1);
      } else {
        setIsPlaying(false);
      }
    } else {
      setIsPlaying(false);
    }
  };
  const handleNext = () => {
    const next = nextCallToPlay(callsData, currentCallId);
    if (next) playCall({ call: next });
  };
  const handlePrev = () => {
    const prev = previousCallToPlay(callsData, currentCallId);
    if (prev) playCall({ call: prev });
  };
  const handleAutoPlay = () => setAutoPlay((v) => !v);

  // Play the socket-selected call when idle + autoplay (from CallPlayer.js).
  useEffect(() => {
    if (selectCallId && callsData && !isPlaying && autoPlay) {
      const call = callsData.entities[selectCallId];
      if (call) { setIsPlaying(true); setCurrentCall(call); dispatch(playedCall(call._id)); }
    }
  }, [selectCallId]);

  // Play the URL-specified initial call once (from CallPlayer.js).
  const [selectedInitial, setSelectedInitial] = useState(false);
  useEffect(() => {
    if (initialCallId && callsData) {
      const call = callsData.entities[initialCallId];
      if (call && !selectedInitial) {
        setIsPlaying(true); setCurrentCall(call); setSelectedInitial(true);
        dispatch(playedCall(call._id));
      }
    }
  }, [initialCallId, callsData]);

  // --- lifecycle effects (verbatim from Calls.js) ---
  useEffect(() => {
    setStateFromUri();
    return () => stopSocket();
  }, []);

  useEffect(() => {
    socket.on("connect", () => setIsConnected(true));
    socket.on("disconnect", () => setIsConnected(false));
    socket.on("reconnect", () => setIsConnected(true));
    socket.on("new message", handleSocketMessage);
    socket.on("transcript", handleTranscript);
    return () => { socket.off("connect"); socket.off("disconnect"); socket.off("new message"); socket.off("transcript"); };
  }, []);

  useEffect(() => {
    if (filterTimeframe && (filterTimeframe.from || filterTimeframe.to)) setLive(false);
  }, [filterTimeframe]);

  useEffect(() => { dispatch(getCalls({})); },
    [shortName, filterGroupId, filterTalkgroups, filterUnit, filterRows, filterTimeframe, filterType, filterDate, filterStarred, live]);

  useEffect(() => { if (live && isConnected) startSocket(); },
    [shortName, filterGroupId, filterTalkgroups, filterUnit, filterRows, filterTimeframe, filterType, filterDate, filterStarred, isConnected, live]);

  useEffect(() => {
    const reconnected = isConnected && !wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (reconnected && live && window.scrollY <= SCROLL_TOP_THRESHOLD) dispatch(getCalls({}));
  }, [isConnected, live]);

  useEffect(() => { updateUri(); },
    [filterGroupId, filterTalkgroups, filterUnit, filterRows, filterTimeframe, filterType, filterDate, filterStarred, selectCallId, initialCallId]);

  return (
    <div className="demo-root">
      <DemoTopBar
        system={system} shortName={shortName} live={live} isConnected={isConnected}
        archiveLabel={archiveLabel} onLiveToggle={handleLiveToggle} onArchivePick={handleArchivePick}
        onActivityToggle={() => setActivityVisible((v) => !v)}
        activityVisible={activityVisible} socket={socket}
      />
      <DemoChips shortName={shortName} />
      <main className="demo-main">
        {system && system.status && system.status.length > 0 && !statusDismissed && (
          <div className="demo-status">
            <span><strong>System status:</strong> {system.status}</span>
            <button onClick={() => setStatusDismissed(true)}>✕</button>
          </div>
        )}
        {activityVisible ? (
          <Activity navigate={handleActivityNavigate} />
        ) : (
          <DemoFeed
            callsData={callsData}
            activeCallId={currentCallId}
            activeCallTime={currentCall ? currentCall.time : null}
            playCall={playCall}
            handleNewer={handleNewer}
            handleOlder={handleOlder}
            live={live}
            shortName={shortName}
            groupsData={groupsData}
          />
        )}
      </main>
      <DemoPlayer
        call={currentCall}
        playSilence={silenceCount}
        autoPlay={autoPlay}
        onAutoPlayToggle={handleAutoPlay}
        onEnded={callEnded}
        onPlayPause={setIsPlaying}
        onNext={handleNext}
        onPrev={handlePrev}
        talkgroupsData={talkgroupsData}
        groupsData={groupsData}
        callsData={callsData}
      />
    </div>
  );
}

export default DemoCalls;
